import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';

export const getChatbotRules = createAsyncThunk(
    'chatbot/getChatbotRules',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.get(API_ENDPOINTS.CHATBOT_RULES, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Rules retrieved successfully',
                    data: data || {}
                };
            }
            return rejectWithValue({ message: message || 'Failed to fetch rules', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to fetch rules' });
        }
    }
);

export const getChatbotRule = createAsyncThunk(
    'chatbot/getChatbotRule',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.get(API_ENDPOINTS.CHATBOT_RULE(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Rule retrieved successfully',
                    rule: data || null
                };
            }
            return rejectWithValue({ message: message || 'Failed to fetch rule', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to fetch rule' });
        }
    }
);

export const createChatbotRule = createAsyncThunk(
    'chatbot/createChatbotRule',
    async (ruleData, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.post(API_ENDPOINTS.CHATBOT_RULES, ruleData);
            const { status, message, data } = response.data;

            if (status === 201 || status === 200) {
                return {
                    success: true,
                    message: message || 'Rule created successfully',
                    rule: data || null
                };
            }
            return rejectWithValue({ message: message || 'Failed to create rule', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to create rule' });
        }
    }
);

export const updateChatbotRule = createAsyncThunk(
    'chatbot/updateChatbotRule',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.put(API_ENDPOINTS.CHATBOT_RULE(id), data);
            const { status, message, data: responseData } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Rule updated successfully',
                    rule: responseData || null
                };
            }
            return rejectWithValue({ message: message || 'Failed to update rule', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to update rule' });
        }
    }
);

export const deleteChatbotRule = createAsyncThunk(
    'chatbot/deleteChatbotRule',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.delete(API_ENDPOINTS.CHATBOT_RULE(id));
            const { status, message } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Rule deleted successfully',
                    id: id
                };
            }
            return rejectWithValue({ message: message || 'Failed to delete rule', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to delete rule' });
        }
    }
);

export const toggleChatbotRule = createAsyncThunk(
    'chatbot/toggleChatbotRule',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.post(API_ENDPOINTS.CHATBOT_RULE_TOGGLE(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Rule toggled successfully',
                    id: id,
                    is_active: data?.is_active || false
                };
            }
            return rejectWithValue({ message: message || 'Failed to toggle rule', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to toggle rule' });
        }
    }
);

export const duplicateChatbotRule = createAsyncThunk(
    'chatbot/duplicateChatbotRule',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.post(API_ENDPOINTS.CHATBOT_RULE_DUPLICATE(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Rule duplicated successfully',
                    rule: data || null
                };
            }
            return rejectWithValue({ message: message || 'Failed to duplicate rule', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to duplicate rule' });
        }
    }
);

export const getChatbotStats = createAsyncThunk(
    'chatbot/getChatbotStats',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.get(API_ENDPOINTS.CHATBOT_STATS, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Statistics retrieved',
                    stats: data || {}
                };
            }
            return rejectWithValue({ message: message || 'Failed to fetch statistics', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to fetch statistics' });
        }
    }
);

export const getChatbotTopKeywords = createAsyncThunk(
    'chatbot/getChatbotTopKeywords',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.get(API_ENDPOINTS.CHATBOT_TOP_KEYWORDS, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Top keywords retrieved',
                    keywords: data || []
                };
            }
            return rejectWithValue({ message: message || 'Failed to fetch top keywords', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to fetch top keywords' });
        }
    }
);

export const getChatbotFallback = createAsyncThunk(
    'chatbot/getChatbotFallback',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.get(API_ENDPOINTS.CHATBOT_FALLBACK, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Fallback reply retrieved',
                    fallback: data || null
                };
            }
            return rejectWithValue({ message: message || 'Failed to fetch fallback reply', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to fetch fallback reply' });
        }
    }
);

export const saveChatbotFallback = createAsyncThunk(
    'chatbot/saveChatbotFallback',
    async ({ message, organization_id }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.post(API_ENDPOINTS.CHATBOT_FALLBACK, {
                message,
                organization_id
            });
            const { status, message: responseMessage, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: responseMessage || 'Fallback reply saved',
                    fallback: data || null
                };
            }
            return rejectWithValue({ message: responseMessage || 'Failed to save fallback reply', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to save fallback reply' });
        }
    }
);

export const getChatbotConversations = createAsyncThunk(
    'chatbot/getChatbotConversations',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            const response = await apiClient.get(API_ENDPOINTS.CHATBOT_CONVERSATIONS, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Conversations retrieved',
                    data: data || {}
                };
            }
            return rejectWithValue({ message: message || 'Failed to fetch conversations', status });
        } catch (error) {
            return rejectWithValue({ message: error.response?.data?.message || 'Failed to fetch conversations' });
        }
    }
);

// =============================================
// SLICE
// =============================================

const initialState = {
    rules: [],
    pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
    },
    currentRule: null,
    stats: null,
    topKeywords: [],
    fallback: null,
    conversations: [],
    conversationsPagination: {},
    isLoading: false,
    isSubmitting: false,
    error: null,
    successMessage: null,
    apiMessage: null,
};

const chatbotSlice = createSlice({
    name: 'chatbot',
    initialState,
    reducers: {
        clearError: (state) => { state.error = null; },
        clearSuccessMessage: (state) => { state.successMessage = null; },
        clearApiMessage: (state) => { state.apiMessage = null; },
        clearChatbotState: (state) => {
            state.rules = [];
            state.pagination = { current_page: 1, last_page: 1, per_page: 15, total: 0 };
            state.currentRule = null;
            state.stats = null;
            state.topKeywords = [];
            state.fallback = null;
            state.conversations = [];
            state.error = null;
            state.successMessage = null;
            state.apiMessage = null;
            state.isLoading = false;
            state.isSubmitting = false;
        },
        clearCurrentRule: (state) => {
            state.currentRule = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Get rules
            .addCase(getChatbotRules.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(getChatbotRules.fulfilled, (state, action) => {
                state.isLoading = false;
                state.rules = action.payload.data?.data || [];
                state.pagination = action.payload.data?.meta || initialState.pagination;
                state.apiMessage = action.payload.message;
                state.successMessage = action.payload.message;
                state.error = null;
            })
            .addCase(getChatbotRules.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch rules';
                state.apiMessage = action.payload?.message || 'Failed to fetch rules';
                state.rules = [];
            })

            // Create rule
            .addCase(createChatbotRule.pending, (state) => {
                state.isSubmitting = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(createChatbotRule.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
                if (action.payload.rule) {
                    state.rules = [action.payload.rule, ...state.rules];
                }
            })
            .addCase(createChatbotRule.rejected, (state, action) => {
                state.isSubmitting = false;
                state.error = action.payload?.message || 'Failed to create rule';
                state.apiMessage = action.payload?.message || 'Failed to create rule';
            })

            // Update rule
            .addCase(updateChatbotRule.pending, (state) => {
                state.isSubmitting = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(updateChatbotRule.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
                if (action.payload.rule) {
                    state.rules = state.rules.map(r =>
                        r.id === action.payload.rule.id ? action.payload.rule : r
                    );
                    if (state.currentRule?.id === action.payload.rule.id) {
                        state.currentRule = action.payload.rule;
                    }
                }
            })
            .addCase(updateChatbotRule.rejected, (state, action) => {
                state.isSubmitting = false;
                state.error = action.payload?.message || 'Failed to update rule';
                state.apiMessage = action.payload?.message || 'Failed to update rule';
            })

            // Delete rule
            .addCase(deleteChatbotRule.pending, (state) => {
                state.isSubmitting = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(deleteChatbotRule.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
                if (action.payload.id) {
                    state.rules = state.rules.filter(r => r.id !== action.payload.id);
                    if (state.currentRule?.id === action.payload.id) {
                        state.currentRule = null;
                    }
                }
            })
            .addCase(deleteChatbotRule.rejected, (state, action) => {
                state.isSubmitting = false;
                state.error = action.payload?.message || 'Failed to delete rule';
                state.apiMessage = action.payload?.message || 'Failed to delete rule';
            })

            // Toggle rule
            .addCase(toggleChatbotRule.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(toggleChatbotRule.fulfilled, (state, action) => {
                state.isLoading = false;
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
                if (action.payload.id) {
                    state.rules = state.rules.map(r =>
                        r.id === action.payload.id ? { ...r, is_active: action.payload.is_active } : r
                    );
                }
            })
            .addCase(toggleChatbotRule.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to toggle rule';
                state.apiMessage = action.payload?.message || 'Failed to toggle rule';
            })

            // Duplicate rule
            .addCase(duplicateChatbotRule.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(duplicateChatbotRule.fulfilled, (state, action) => {
                state.isLoading = false;
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
                if (action.payload.rule) {
                    state.rules = [action.payload.rule, ...state.rules];
                }
            })
            .addCase(duplicateChatbotRule.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to duplicate rule';
                state.apiMessage = action.payload?.message || 'Failed to duplicate rule';
            })

            // Get stats
            .addCase(getChatbotStats.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(getChatbotStats.fulfilled, (state, action) => {
                state.isLoading = false;
                state.stats = action.payload.stats || null;
                state.apiMessage = action.payload.message;
                state.error = null;
            })
            .addCase(getChatbotStats.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch statistics';
                state.apiMessage = action.payload?.message || 'Failed to fetch statistics';
                state.stats = null;
            })

            // Get top keywords
            .addCase(getChatbotTopKeywords.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(getChatbotTopKeywords.fulfilled, (state, action) => {
                state.isLoading = false;
                state.topKeywords = action.payload.keywords || [];
                state.apiMessage = action.payload.message;
                state.error = null;
            })
            .addCase(getChatbotTopKeywords.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch top keywords';
                state.apiMessage = action.payload?.message || 'Failed to fetch top keywords';
                state.topKeywords = [];
            })

            // Get fallback
            .addCase(getChatbotFallback.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(getChatbotFallback.fulfilled, (state, action) => {
                state.isLoading = false;
                state.fallback = action.payload.fallback || null;
                state.apiMessage = action.payload.message;
                state.error = null;
            })
            .addCase(getChatbotFallback.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch fallback reply';
                state.apiMessage = action.payload?.message || 'Failed to fetch fallback reply';
                state.fallback = null;
            })

            // Save fallback
            .addCase(saveChatbotFallback.pending, (state) => {
                state.isSubmitting = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(saveChatbotFallback.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
                state.fallback = action.payload.fallback || null;
            })
            .addCase(saveChatbotFallback.rejected, (state, action) => {
                state.isSubmitting = false;
                state.error = action.payload?.message || 'Failed to save fallback reply';
                state.apiMessage = action.payload?.message || 'Failed to save fallback reply';
            })

            // Get conversations
            .addCase(getChatbotConversations.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.apiMessage = null;
            })
            .addCase(getChatbotConversations.fulfilled, (state, action) => {
                state.isLoading = false;
                state.conversations = action.payload.data?.data || [];
                state.conversationsPagination = action.payload.data?.meta || {};
                state.apiMessage = action.payload.message;
                state.error = null;
            })
            .addCase(getChatbotConversations.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch conversations';
                state.apiMessage = action.payload?.message || 'Failed to fetch conversations';
                state.conversations = [];
                state.conversationsPagination = {};
            });
    },
});

export const {
    clearError,
    clearSuccessMessage,
    clearApiMessage,
    clearChatbotState,
    clearCurrentRule,
} = chatbotSlice.actions;

export const selectChatbotRules = (state) => state.chatbot?.rules || [];
export const selectChatbotPagination = (state) => state.chatbot?.pagination || { current_page: 1, last_page: 1, per_page: 15, total: 0 };
export const selectCurrentChatbotRule = (state) => state.chatbot?.currentRule || null;
export const selectChatbotStats = (state) => state.chatbot?.stats || null;
export const selectChatbotTopKeywords = (state) => state.chatbot?.topKeywords || [];
export const selectChatbotFallback = (state) => state.chatbot?.fallback || null;
export const selectChatbotConversations = (state) => state.chatbot?.conversations || [];
export const selectChatbotConversationsPagination = (state) => state.chatbot?.conversationsPagination || {};
export const selectChatbotLoading = (state) => state.chatbot?.isLoading || false;
export const selectChatbotSubmitting = (state) => state.chatbot?.isSubmitting || false;
export const selectChatbotError = (state) => state.chatbot?.error || null;
export const selectChatbotApiMessage = (state) => state.chatbot?.apiMessage || null;

export default chatbotSlice.reducer;